'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { Search, ArrowRight, ChevronDown } from 'lucide-react'
import { LinkButton } from '@/components/ui/button'
import { SignInLink } from '@/components/platform/SignInLink'
import { AnimatedThemeToggle } from '@/components/ui/AnimatedThemeToggle'
import { LocaleSwitcher } from '@/components/LocaleSwitcher'
import { useSearch } from '@/hooks/useSearch'
import { getCategoryLabel } from '@/lib/search-index'

/** Ignore scroll deltas below this — trackpad jitter and iOS rubber-banding. */
const SCROLL_DIRECTION_THRESHOLD = 8

/** Above this offset the bar is always shown, so the top of every page and
 *  any page too short to scroll keeps its navigation. Matches the bar's own
 *  h-16 plus a little slack. */
const HEADER_REVEAL_ABOVE = 80

/**
 * Width at which the full desktop navigation replaces the hamburger. MUST stay
 * equal to the Tailwind `lg:` breakpoint used on the nav, the right-side
 * actions, the hamburger, and the mobile overlay/panel in this file.
 *
 * Moved from `xl` (1280px) on 2026-09-20: below 1280 the entire navigation,
 * search, theme toggle, Sign in and the "Get in Touch" CTA collapsed into the
 * hamburger, so iPad landscape (1180px) and many 13-inch laptops never saw the
 * primary nav or the header CTA at all (heuristic evaluation F25).
 *
 * 1024px is the tight case: 8 nav items plus 5 right-side controls, with
 * `text-sm` already at the minimum readable size, leaves spacing as the only
 * lever. Hence `lg:gap-3` on the nav and `lg:gap-2` on the actions, both
 * widening again from `xl`. Verify at 1024, 1180 and 1279 before shipping.
 */
const DESKTOP_NAV_BREAKPOINT_PX = 1024

const navigationConfig = {
  mainNav: [
    { label: 'Home', href: '/' },
    {
      label: 'Products',
      href: '/products',
      // Order: the overview, then the suite sequence Radar (measure) ->
      // Vector (act) -> Hive (scale) as one uninterrupted run, then the two
      // Radar sub-items last. Radar Sites is a Radar capability rather than a
      // fourth product, so it sits with the sample report and not inside the
      // three-product sequence.
      children: [
        {
          label: 'All Products',
          href: '/products',
          description: 'Overview of Radar, Vector, and Hive',
        },
        {
          label: 'Radar: AI Visibility',
          href: '/platform',
          description: 'DIY AI visibility audit + implementation',
        },
        {
          label: 'Vector: Lead Qualification',
          href: '/vector',
          description: '12-dimension scoring for B2B leads',
        },
        {
          label: 'Hive: AI Co-workers',
          href: '/hive',
          description: 'Multi-agent teams that run operations',
        },
        {
          label: 'Radar Sites',
          href: '/radar-sites',
          description: 'AI website builder, inside Radar',
        },
        {
          label: 'Radar Sample Report',
          href: '/platform/sample-report',
          description: 'A full client audit, published end to end',
        },
      ],
    },
    {
      label: 'Services',
      href: '/services',
      children: [
        {
          label: 'All Services',
          href: '/services',
          description: 'Overview of all 4 service tracks',
        },
        {
          label: 'Consulting',
          href: '/consulting',
          description: 'White-label websites and AI visibility execution',
        },
        {
          label: 'AI Visibility Strategy',
          href: '/services/ai-visibility-strategy',
          description: 'GEO, AEO, and LLM optimization sprint + retainer',
        },
        {
          label: 'AI Product Development',
          href: '/services/ai-product-development',
          description: 'Ship AI products in a 90-day launch window',
        },
        {
          label: 'Brand & Sales Design',
          href: '/services/revenue-first-design',
          description: 'Brand identity, design systems, pitch decks',
        },
        {
          label: 'Growth Marketing',
          href: '/services/ai-powered-growth',
          description: 'Pipeline generation for B2B SaaS, retainer-based',
        },
      ],
    },
    {
      label: 'Tools',
      href: '/tools',
      children: [
        // Radar lives under Products only. It was listed here too, under a
        // second label ("Radar Platform"), which read as a different page
        // and split the internal anchor text for /platform.
        {
          label: 'AEO Page Auditor',
          href: '/tools/aeo-page-auditor',
          description: 'Answer engine readiness score',
        },
        {
          label: 'Answer Engine Tester',
          href: '/tools/answer-engine-tester',
          description: 'Test if AI cites your page',
        },
        {
          label: 'AI Citation Tracker',
          href: '/tools/ai-citation-tracker',
          description: 'Check if AI engines cite your brand',
        },
        {
          label: 'All Tools',
          href: '/tools',
          description: 'View all free tools',
        },
      ],
    },
    { label: 'Projects', href: '/projects' },
    { label: 'About', href: '/about' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blog', href: '/blogs' },
  ],
  ctaButton: {
    // Persistent CTA carries the contact path; the free-Radar funnel is
    // served by the hero and section CTAs on the page itself.
    label: 'Get in Touch',
    href: '/contact-us',
  },
}

export default function Header() {
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isWorksOpen, setIsWorksOpen] = useState(false)
  const [isToolsOpen, setIsToolsOpen] = useState(false)
  const [isBlogOpen, setIsBlogOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)
  const [isMobileWorksOpen, setIsMobileWorksOpen] = useState(false)
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false)
  const [isMobileBlogOpen, setIsMobileBlogOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isHiddenByScroll, setIsHiddenByScroll] = useState(false)
  const lastScrollYRef = useRef(0)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [searchFocusedIndex, setSearchFocusedIndex] = useState<number>(-1)
  const pathname = usePathname()
  const { query, setQuery, results, isSearching } = useSearch()

  // Refs for keyboard navigation
  const productsRef = useRef<HTMLDivElement>(null)
  const servicesRef = useRef<HTMLDivElement>(null)
  const projectsRef = useRef<HTMLDivElement>(null)
  const toolsRef = useRef<HTMLDivElement>(null)
  const blogsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  // Helper function to check if nav item is active
  const isActiveNav = (href: string, hasChildren?: boolean, label?: string) => {
    if (href === '/' && pathname === '/') return true
    if (href !== '/' && pathname.startsWith(href)) return true
    if (
      hasChildren &&
      label === 'Products' &&
      (pathname.startsWith('/vector') || pathname.startsWith('/hive'))
    )
      return true
    if (
      hasChildren &&
      label === 'Services' &&
      (pathname.startsWith('/services') || pathname.startsWith('/consulting'))
    )
      return true
    if (hasChildren && label === 'Projects' && pathname.startsWith('/projects'))
      return true
    if (hasChildren && label === 'Tools' && pathname.startsWith('/tools'))
      return true
    if (hasChildren && label === 'Blog' && pathname.startsWith('/blogs'))
      return true
    return false
  }

  const productsNav = navigationConfig.mainNav.find(
    item => item.label === 'Products'
  )
  const productsItems = productsNav?.children || []

  const servicesNav = navigationConfig.mainNav.find(
    item => item.label === 'Services'
  )
  const servicesItems = servicesNav?.children || []

  const worksNav = navigationConfig.mainNav.find(
    item => item.label === 'Projects'
  )
  const worksItems = worksNav?.children || []

  const toolsNav = navigationConfig.mainNav.find(item => item.label === 'Tools')
  const toolsItems = toolsNav?.children || []

  const blogNav = navigationConfig.mainNav.find(item => item.label === 'Blog')
  const blogItems = blogNav?.children || []

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  // Close mobile menu when clicking outside or on window resize
  useEffect(() => {
    const handleResize = () => {
      // Must match the `lg:` breakpoint the desktop nav appears at (1024px).
      // If these two ever disagree, the mobile panel stays mounted underneath
      // a visible desktop nav.
      if (window.innerWidth >= DESKTOP_NAV_BREAKPOINT_PX && isMobileMenuOpen) {
        setIsMobileMenuOpen(false)
        setIsMobileProductsOpen(false)
        setIsMobileServicesOpen(false)
        setIsMobileWorksOpen(false)
        setIsMobileToolsOpen(false)
        setIsMobileBlogOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMobileMenuOpen])

  // Mobile menu dialog: trap focus, close on Escape, restore focus on close
  useEffect(() => {
    if (!isMobileMenuOpen) return
    const panel = mobileMenuRef.current
    if (!panel) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    const getFocusable = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null)

    // Move focus into the dialog
    panel.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsMobileMenuOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const focusable = getFocusable()
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus?.()
    }
  }, [isMobileMenuOpen])

  // Scroll state: the opaque-background threshold, plus the direction that
  // drives hide-on-down / reveal-on-up.
  //
  // Both live on ONE passive listener because they read the same scrollY on
  // the same frame; a second listener would be a second layout read per
  // scroll event for no gain.
  useEffect(() => {
    // Seeded from the live position, never from 0: a reload part-way down the
    // page would otherwise register as one huge downward delta and start with
    // the bar already hidden.
    setIsScrolled(window.scrollY > 10)
    lastScrollYRef.current = window.scrollY

    // Deliberately synchronous, NOT rAF-coalesced. iOS Safari throttles
    // requestAnimationFrame during momentum scrolling, so a rAF-gated handler
    // stops updating mid-flick: `isScrolled` goes stale and the bar stays
    // transparent over the content it is sitting on. Reading scrollY in a
    // passive listener is cheap and does not force layout; React bails out of
    // the re-render when the booleans are unchanged, which is most events.
    const handleScroll = () => {
      const y = window.scrollY
      setIsScrolled(y > 10)

      const delta = y - lastScrollYRef.current
      // Below the jitter floor the direction is not a real intent — trackpad
      // noise and iOS rubber-banding both land here. Leave lastScrollY alone
      // so small movements accumulate instead of being swallowed.
      if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD) return
      lastScrollYRef.current = y

      // The bar never hides inside its own height: at the top of a page, or
      // on a page too short to scroll meaningfully, it simply stays.
      if (y <= HEADER_REVEAL_ABOVE) {
        setIsHiddenByScroll(false)
        return
      }
      setIsHiddenByScroll(delta > 0)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD+K or CTRL+K to toggle search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(prev => !prev)
        setIsServicesOpen(false)
        setIsWorksOpen(false)
        setIsToolsOpen(false)
        setIsBlogOpen(false)
      }
      // ESC to close search
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false)
        setQuery('')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSearchOpen, setQuery])

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        isSearchOpen &&
        !target.closest('[data-search-panel]') &&
        !target.closest('[data-search-button]')
      ) {
        setIsSearchOpen(false)
      }
      // Close dropdowns when clicking outside
      if (
        (isProductsOpen ||
          isServicesOpen ||
          isWorksOpen ||
          isToolsOpen ||
          isBlogOpen) &&
        !target.closest('[data-dropdown]') &&
        !target.closest('[data-dropdown-trigger]')
      ) {
        setIsProductsOpen(false)
        setIsServicesOpen(false)
        setIsWorksOpen(false)
        setIsToolsOpen(false)
        setIsBlogOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [
    isSearchOpen,
    isProductsOpen,
    isServicesOpen,
    isWorksOpen,
    isToolsOpen,
    isBlogOpen,
  ])

  // Keyboard navigation for dropdowns
  useEffect(() => {
    const handleDropdownKeyDown = (e: KeyboardEvent) => {
      if (
        !isProductsOpen &&
        !isServicesOpen &&
        !isWorksOpen &&
        !isToolsOpen &&
        !isBlogOpen
      )
        return

      const currentItems =
        (isProductsOpen && productsItems) ||
        (isServicesOpen && servicesItems) ||
        (isWorksOpen && worksItems) ||
        (isToolsOpen && toolsItems) ||
        (isBlogOpen && blogItems) ||
        []

      const currentMenu = isProductsOpen
        ? productsRef.current
        : isServicesOpen
          ? servicesRef.current
          : isWorksOpen
            ? projectsRef.current
            : isToolsOpen
              ? toolsRef.current
              : blogsRef.current

      const focusMenuItem = (index: number) => {
        currentMenu
          ?.querySelectorAll<HTMLElement>('[role="menuitem"]')
          [index]?.focus()
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const nextIndex =
          focusedIndex < currentItems.length - 1 ? focusedIndex + 1 : 0
        setFocusedIndex(nextIndex)
        focusMenuItem(nextIndex)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const nextIndex =
          focusedIndex > 0 ? focusedIndex - 1 : currentItems.length - 1
        setFocusedIndex(nextIndex)
        focusMenuItem(nextIndex)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        const trigger = document.querySelector<HTMLElement>(
          '[data-dropdown-trigger][aria-expanded="true"]'
        )
        setIsProductsOpen(false)
        setIsServicesOpen(false)
        setIsWorksOpen(false)
        setIsToolsOpen(false)
        setIsBlogOpen(false)
        setFocusedIndex(-1)
        requestAnimationFrame(() => trigger?.focus())
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault()
        const item = currentItems[focusedIndex]
        if (item?.href) {
          window.location.href = item.href
        }
      }
    }

    if (
      isProductsOpen ||
      isServicesOpen ||
      isWorksOpen ||
      isToolsOpen ||
      isBlogOpen
    ) {
      window.addEventListener('keydown', handleDropdownKeyDown)
      return () => window.removeEventListener('keydown', handleDropdownKeyDown)
    }
  }, [
    isProductsOpen,
    isServicesOpen,
    isWorksOpen,
    isToolsOpen,
    isBlogOpen,
    focusedIndex,
    productsItems,
    servicesItems,
    worksItems,
    toolsItems,
    blogItems,
  ])

  // Keyboard navigation for search dropdown
  useEffect(() => {
    const handleSearchKeyDown = (e: KeyboardEvent) => {
      if (!isSearchOpen) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (searchInputRef.current === document.activeElement) {
          setSearchFocusedIndex(0)
        } else {
          setSearchFocusedIndex(prev =>
            prev < results.length - 1 ? prev + 1 : 0
          )
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (searchFocusedIndex === 0) {
          searchInputRef.current?.focus()
          setSearchFocusedIndex(-1)
        } else {
          setSearchFocusedIndex(prev =>
            prev > 0 ? prev - 1 : results.length - 1
          )
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsSearchOpen(false)
        setQuery('')
        setSearchFocusedIndex(-1)
      } else if (e.key === 'Enter' && searchFocusedIndex >= 0) {
        e.preventDefault()
        const item = results[searchFocusedIndex]
        if (item?.url) {
          window.location.href = item.url
        }
      }
    }

    if (isSearchOpen) {
      window.addEventListener('keydown', handleSearchKeyDown)
      return () => window.removeEventListener('keydown', handleSearchKeyDown)
    }
  }, [isSearchOpen, searchFocusedIndex, results, setQuery])

  // Focus search input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [isSearchOpen])

  // Reset focused index when dropdowns close
  useEffect(() => {
    if (
      !isProductsOpen &&
      !isServicesOpen &&
      !isWorksOpen &&
      !isToolsOpen &&
      !isBlogOpen
    ) {
      setFocusedIndex(-1)
    }
  }, [isProductsOpen, isServicesOpen, isWorksOpen, isToolsOpen, isBlogOpen])

  const headerStyle = {
    backgroundColor: isScrolled ? 'var(--background)' : 'transparent',
  }

  // An open panel always outranks the scroll direction. The mega menus and the
  // search panel are children of <header>, so they ride its transform — hiding
  // the bar with one open would slide the open panel off screen with it.
  // Derived rather than stored so the two can never disagree.
  const headerHidden =
    isHiddenByScroll &&
    !isProductsOpen &&
    !isServicesOpen &&
    !isWorksOpen &&
    !isToolsOpen &&
    !isBlogOpen &&
    !isSearchOpen &&
    !isMobileMenuOpen

  return (
    <header
      // Reveal whenever focus lands anywhere inside the bar. Without this a
      // keyboard user tabbing in from the skip link would move focus onto a
      // control that is translated off screen — the same defect `inert` fixes
      // for the collapsed panels.
      onFocusCapture={() => setIsHiddenByScroll(false)}
      className={`sticky top-0 transition-[translate,background-color] motion-reduce:transition-none ${
        isMobileMenuOpen ? 'z-[1100]' : 'z-40'
      } ${
        headerHidden
          ? // Leaving: short, accelerating — it should get out of the way.
            '-translate-y-full duration-200 ease-[cubic-bezier(0.4,0,1,1)]'
          : // Arriving: longer, decelerating (expo-out) so it settles rather
            // than stops. Asymmetry is what makes the pair feel considered;
            // one shared curve reads as mechanical.
            //
            // NO `translate-y-0` here, deliberately. Any translate other than
            // `none` makes this element the containing block for its
            // position:fixed descendants — and the mobile menu overlay and
            // panel are BOTH children of <header>. With a resting translate
            // set, their `fixed inset-0` resolved to the 64px header bar
            // instead of the viewport, so the panel collapsed to a strip and
            // the page showed through it. Omitting the class leaves
            // `translate: none` at rest; the transition still runs because
            // `none` interpolates as the identity.
            'duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]'
      }`}
      style={headerStyle}
    >
      <div
        data-header-bar
        className='max-w-hero mx-auto px-4 min-[1344px]:px-8'
      >
        <nav className='flex h-16 items-center justify-between relative'>
          {/* Logo */}
          <Link
            href='/'
            className='flex min-h-11 shrink-0 items-center hover:opacity-80 transition-opacity z-50'
          >
            <Image
              src='/pixelmojo-branding-new.svg'
              alt='Pixelmojo'
              width={140}
              height={32}
              className='h-8 w-auto transition-all duration-300'
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <div className='hidden lg:flex items-center justify-center flex-1 min-w-0'>
            <div className='flex items-center whitespace-nowrap gap-6 lg:gap-3 xl:gap-[clamp(1.5rem,calc(10vw_-_6.5rem),2.5rem)] 2xl:gap-12'>
              {navigationConfig.mainNav.map(item =>
                item.children ? (
                  // Dropdown items (Services/Projects)
                  <div key={item.label} className='relative'>
                    <button
                      data-dropdown-trigger
                      onClick={() => {
                        if (item.label === 'Products') {
                          setIsProductsOpen(!isProductsOpen)
                          setIsServicesOpen(false)
                          setIsWorksOpen(false)
                          setIsToolsOpen(false)
                          setIsBlogOpen(false)
                          setIsSearchOpen(false)
                          setFocusedIndex(-1)
                        } else if (item.label === 'Services') {
                          setIsServicesOpen(!isServicesOpen)
                          setIsProductsOpen(false)
                          setIsWorksOpen(false)
                          setIsToolsOpen(false)
                          setIsBlogOpen(false)
                          setIsSearchOpen(false)
                          setFocusedIndex(-1)
                        } else if (item.label === 'Projects') {
                          setIsWorksOpen(!isWorksOpen)
                          setIsProductsOpen(false)
                          setIsServicesOpen(false)
                          setIsToolsOpen(false)
                          setIsBlogOpen(false)
                          setIsSearchOpen(false)
                          setFocusedIndex(-1)
                        } else if (item.label === 'Tools') {
                          setIsToolsOpen(!isToolsOpen)
                          setIsProductsOpen(false)
                          setIsServicesOpen(false)
                          setIsWorksOpen(false)
                          setIsBlogOpen(false)
                          setIsSearchOpen(false)
                          setFocusedIndex(-1)
                        } else if (item.label === 'Blog') {
                          setIsBlogOpen(!isBlogOpen)
                          setIsProductsOpen(false)
                          setIsServicesOpen(false)
                          setIsWorksOpen(false)
                          setIsToolsOpen(false)
                          setIsSearchOpen(false)
                          setFocusedIndex(-1)
                        }
                      }}
                      aria-expanded={
                        (item.label === 'Products' && isProductsOpen) ||
                        (item.label === 'Services' && isServicesOpen) ||
                        (item.label === 'Projects' && isWorksOpen) ||
                        (item.label === 'Tools' && isToolsOpen) ||
                        (item.label === 'Blog' && isBlogOpen)
                      }
                      aria-haspopup='true'
                      className='flex min-h-11 items-center gap-1 text-sm font-medium transition-colors duration-200 relative'
                      style={{
                        color: isActiveNav(item.href, true, item.label)
                          ? 'var(--primary-ink)'
                          : 'var(--foreground)',
                      }}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          (item.label === 'Products' && isProductsOpen) ||
                          (item.label === 'Services' && isServicesOpen) ||
                          (item.label === 'Projects' && isWorksOpen) ||
                          (item.label === 'Tools' && isToolsOpen) ||
                          (item.label === 'Blog' && isBlogOpen)
                            ? 'rotate-180'
                            : ''
                        }`}
                      />
                    </button>
                    {isActiveNav(item.href, true, item.label) && (
                      <div
                        className='absolute -bottom-1 left-0 right-0 h-0.5 rounded-full'
                        style={{
                          backgroundColor: 'var(--primary)',
                        }}
                      />
                    )}
                  </div>
                ) : (
                  // Regular nav items
                  <div key={item.label} className='relative'>
                    <Link
                      href={item.href}
                      className='inline-flex min-h-11 items-center text-sm font-medium transition-colors duration-200 relative'
                      style={{
                        color: isActiveNav(item.href)
                          ? 'var(--primary-ink)'
                          : 'var(--foreground)',
                      }}
                    >
                      {item.label}
                    </Link>
                    {isActiveNav(item.href) && (
                      <div
                        className='absolute -bottom-1 left-0 right-0 h-0.5 rounded-full'
                        style={{
                          backgroundColor: 'var(--primary)',
                        }}
                      />
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Right Side Actions */}
          <div className='hidden lg:flex shrink-0 items-center gap-2 xl:gap-4'>
            {/* Search Icon */}
            <div className='relative'>
              <button
                data-search-button
                onClick={() => {
                  setIsSearchOpen(!isSearchOpen)
                  setIsProductsOpen(false)
                  setIsServicesOpen(false)
                  setIsWorksOpen(false)
                  setIsToolsOpen(false)
                  setIsBlogOpen(false)
                }}
                className='inline-flex min-h-11 min-w-11 items-center justify-center p-2 transition-colors duration-200 rounded-full hover:bg-muted focus-visible:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary text-foreground'
                aria-label='Search'
              >
                <Search className='w-5 h-5' />
              </button>
            </div>

            {/* Language switcher — renders only on routes with a translation */}
            <LocaleSwitcher />

            {/* Theme Toggle */}
            <AnimatedThemeToggle className='min-h-11 min-w-11' />

            {/* Dynamic Sign in / Account link — entry point for returning
                paid Radar users. Swaps label based on session state. */}
            <SignInLink
              className='inline-flex min-h-11 items-center whitespace-nowrap text-sm font-medium transition-opacity duration-200 hover:opacity-70'
              style={{ color: 'var(--foreground)' }}
            />

            {/* Desktop CTA with dynamic variant */}
            <div>
              <LinkButton
                href={navigationConfig.ctaButton.href}
                data-cta-id='header'
                variant={isScrolled ? 'default' : 'outline'}
                size='default'
                className='transition-all duration-300'
              >
                {navigationConfig.ctaButton.label}
              </LinkButton>
            </div>
          </div>

          {/* Mobile Hamburger Button - Fixed visibility */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className='lg:hidden relative z-50 p-3 -mr-3 touch-manipulation'
            style={{ minHeight: '44px', minWidth: '44px' }}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls='mobile-menu'
          >
            <div className='relative w-6 h-5'>
              <span
                className={`absolute left-0 w-full h-0.5 transition-all duration-300 ease-out ${
                  isMobileMenuOpen ? 'top-2.5 rotate-45' : 'top-0'
                }`}
                style={{
                  backgroundColor: 'var(--foreground)',
                }}
              />
              <span
                className={`absolute left-0 top-2.5 w-full h-0.5 transition-all duration-300 ease-out ${
                  isMobileMenuOpen
                    ? 'opacity-0 scale-0'
                    : 'opacity-100 scale-100'
                }`}
                style={{
                  backgroundColor: 'var(--foreground)',
                }}
              />
              <span
                className={`absolute left-0 w-full h-0.5 transition-all duration-300 ease-out ${
                  isMobileMenuOpen ? 'top-2.5 -rotate-45' : 'top-5'
                }`}
                style={{
                  backgroundColor: 'var(--foreground)',
                }}
              />
            </div>
          </button>
        </nav>
      </div>

      {/* Desktop Products Mega Menu Panel */}
      <div
        data-dropdown
        inert={!isProductsOpen}
        ref={productsRef}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isProductsOpen ? 'max-h-48 border-y opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
        role='menu'
        aria-label='Products menu'
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-6'>
          <div className='grid grid-cols-6 gap-4'>
            {productsItems.map((item, index) => {
              const isFocused = isProductsOpen && focusedIndex === index
              const description =
                'description' in item ? String(item.description) : ''
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setIsProductsOpen(false)
                    setFocusedIndex(-1)
                  }}
                  role='menuitem'
                  tabIndex={
                    isProductsOpen &&
                    (focusedIndex === index ||
                      (focusedIndex < 0 && index === 0))
                      ? 0
                      : -1
                  }
                  className={`group flex min-w-0 flex-col gap-1 transition-all duration-200 px-6 py-4 rounded-xl ${
                    isFocused ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: isFocused
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <span
                    className='font-semibold text-sm'
                    style={{
                      color: isFocused
                        ? 'var(--primary-ink)'
                        : 'var(--foreground)',
                    }}
                  >
                    {item.label}
                  </span>
                  {description && (
                    <span
                      className='text-xs'
                      style={{
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      {description}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Desktop Services Mega Menu Panel */}
      <div
        data-dropdown
        inert={!isServicesOpen}
        ref={servicesRef}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isServicesOpen ? 'max-h-64 border-y opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
        role='menu'
        aria-label='Services menu'
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-4'>
          <div className='flex flex-wrap gap-3 lg:gap-4 xl:gap-6 2xl:gap-8 justify-center'>
            {servicesItems.map((item, index) => {
              const isFocused = isServicesOpen && focusedIndex === index
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setIsServicesOpen(false)
                    setFocusedIndex(-1)
                  }}
                  role='menuitem'
                  tabIndex={
                    isServicesOpen &&
                    (focusedIndex === index ||
                      (focusedIndex < 0 && index === 0))
                      ? 0
                      : -1
                  }
                  className={`group inline-flex min-h-11 items-center text-xs xl:text-sm transition-all duration-200 whitespace-nowrap px-3 xl:px-4 py-2 rounded-lg ${
                    isFocused ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    color: isFocused
                      ? 'var(--primary-ink)'
                      : 'var(--muted-foreground)',
                    backgroundColor: isFocused
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Desktop Projects Mega Menu Panel */}
      <div
        data-dropdown
        inert={!isWorksOpen}
        ref={projectsRef}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isWorksOpen ? 'max-h-40 border-y opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
        role='menu'
        aria-label='Projects menu'
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-4'>
          <div className='flex flex-wrap gap-3 lg:gap-4 xl:gap-6 2xl:gap-8 justify-center'>
            {worksItems.map((item, index) => {
              const isFocused = isWorksOpen && focusedIndex === index
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setIsWorksOpen(false)
                    setFocusedIndex(-1)
                  }}
                  role='menuitem'
                  tabIndex={
                    isWorksOpen &&
                    (focusedIndex === index ||
                      (focusedIndex < 0 && index === 0))
                      ? 0
                      : -1
                  }
                  className={`group inline-flex min-h-11 items-center text-xs xl:text-sm transition-all duration-200 whitespace-nowrap px-3 xl:px-4 py-2 rounded-lg ${
                    isFocused ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    color: isFocused
                      ? 'var(--primary-ink)'
                      : 'var(--muted-foreground)',
                    backgroundColor: isFocused
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Desktop Tools Mega Menu Panel */}
      <div
        data-dropdown
        inert={!isToolsOpen}
        ref={toolsRef}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isToolsOpen ? 'max-h-40 border-y opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
        role='menu'
        aria-label='Tools menu'
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-6'>
          <div className='flex gap-8 justify-center'>
            {toolsItems.map((item, index) => {
              const isFocused = isToolsOpen && focusedIndex === index
              const description =
                'description' in item ? String(item.description) : ''
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setIsToolsOpen(false)
                    setFocusedIndex(-1)
                  }}
                  role='menuitem'
                  tabIndex={
                    isToolsOpen &&
                    (focusedIndex === index ||
                      (focusedIndex < 0 && index === 0))
                      ? 0
                      : -1
                  }
                  className={`group flex flex-col gap-1 transition-all duration-200 px-6 py-4 rounded-xl min-w-[240px] ${
                    isFocused ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: isFocused
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                  }}
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <span
                    className='font-semibold text-sm'
                    style={{
                      color: isFocused
                        ? 'var(--primary-ink)'
                        : 'var(--foreground)',
                    }}
                  >
                    {item.label}
                  </span>
                  {description && (
                    <span
                      className='text-xs'
                      style={{
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      {description}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Desktop Blog Mega Menu Panel */}
      <div
        data-dropdown
        inert={!isBlogOpen}
        ref={blogsRef}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isBlogOpen ? 'max-h-48 border-y opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
        role='menu'
        aria-label='Blogs menu'
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-4'>
          <div className='flex flex-wrap gap-3 lg:gap-4 xl:gap-6 2xl:gap-8 justify-center'>
            {blogItems.map((item, index) => {
              const postDate =
                'date' in item && typeof item.date === 'string'
                  ? item.date
                  : undefined
              const formattedDate = postDate
                ? new Date(postDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : null
              const isFocused = isBlogOpen && focusedIndex === index
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setIsBlogOpen(false)
                    setFocusedIndex(-1)
                  }}
                  role='menuitem'
                  tabIndex={
                    isBlogOpen &&
                    (focusedIndex === index ||
                      (focusedIndex < 0 && index === 0))
                      ? 0
                      : -1
                  }
                  className={`group flex min-h-11 min-w-[180px] flex-col justify-center gap-1 text-xs xl:text-sm transition-all duration-200 px-3 xl:px-4 py-2 rounded-lg ${
                    isFocused ? 'scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    color: isFocused
                      ? 'var(--primary-ink)'
                      : 'var(--muted-foreground)',
                    backgroundColor: isFocused
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  }}
                  title={
                    'fullTitle' in item
                      ? (item as { fullTitle?: string }).fullTitle
                      : item.label
                  }
                  onMouseEnter={() => setFocusedIndex(index)}
                  onMouseLeave={() => setFocusedIndex(-1)}
                >
                  <span className='font-medium truncate'>{item.label}</span>
                  {formattedDate && (
                    <span className='text-[10px] xl:text-xs opacity-70'>
                      {formattedDate}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Desktop Search Mega Menu Panel */}
      <div
        data-search-panel
        inert={!isSearchOpen}
        className={`hidden lg:block w-full overflow-hidden transition-all duration-300 ease-in-out ${
          isSearchOpen
            ? 'max-h-[500px] border-y opacity-100'
            : 'max-h-0 opacity-0'
        }`}
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
        }}
      >
        <div className='max-w-hero mx-auto px-4 min-[1344px]:px-8 py-4'>
          <div className='max-w-3xl mx-auto'>
            {/* Search Input */}
            <div className='mb-4'>
              <div className='relative'>
                <Search
                  className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 opacity-50'
                  style={{
                    color: 'var(--muted-foreground)',
                  }}
                />
                <input
                  ref={searchInputRef}
                  type='text'
                  placeholder='Search blog posts, projects, services...'
                  value={query}
                  onChange={e => {
                    setQuery(e.target.value)
                    setSearchFocusedIndex(-1)
                  }}
                  autoFocus
                  className='w-full pl-10 pr-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all shadow-sm'
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    color: 'var(--foreground)',
                  }}
                  aria-label='Search'
                  aria-expanded={isSearchOpen}
                  aria-controls='search-results'
                />
              </div>
            </div>

            {/* Search Results */}
            {isSearching && (
              <div
                id='search-results'
                role='listbox'
                aria-label='Search results'
              >
                <div className='max-h-[320px] overflow-y-auto'>
                  {results.length > 0 ? (
                    <div className='space-y-1'>
                      {results.map((item, index) => {
                        const isFocused = searchFocusedIndex === index
                        return (
                          <Link
                            key={item.id}
                            href={item.url}
                            onClick={() => {
                              setIsSearchOpen(false)
                              setQuery('')
                              setSearchFocusedIndex(-1)
                            }}
                            role='option'
                            aria-selected={isFocused}
                            tabIndex={isSearchOpen ? 0 : -1}
                            className={`group flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
                              isFocused ? 'scale-[1.02]' : 'hover:scale-[1.02]'
                            }`}
                            style={{
                              backgroundColor: isFocused
                                ? 'rgba(0, 0, 0, 0.08)'
                                : 'rgba(0, 0, 0, 0.03)',
                            }}
                            onMouseEnter={() => setSearchFocusedIndex(index)}
                            onMouseLeave={() => setSearchFocusedIndex(-1)}
                          >
                            <div className='flex-1 min-w-0'>
                              <div
                                className='font-medium text-sm truncate'
                                style={{
                                  color: 'var(--foreground)',
                                }}
                              >
                                {item.title}
                              </div>
                              <div
                                className='text-xs truncate mt-0.5'
                                style={{
                                  color: 'var(--muted-foreground)',
                                }}
                              >
                                {item.description}
                              </div>
                            </div>
                            <div className='flex items-center gap-2 flex-shrink-0'>
                              <span
                                className='text-xs px-2 py-1 rounded-full'
                                style={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                  color: 'var(--muted-foreground)',
                                }}
                              >
                                {getCategoryLabel(item.category)}
                              </span>
                              <ArrowRight
                                className='w-4 h-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0'
                                style={{
                                  color: 'var(--primary)',
                                }}
                              />
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                  ) : (
                    <div
                      className='text-center py-8 text-sm'
                      style={{
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      No results found for "{query}"
                    </div>
                  )}
                </div>

                {/* See All Results Link */}
                {results.length > 0 && (
                  <div
                    className='mt-3 pt-3 border-t'
                    style={{
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Link
                      href={`/search?q=${encodeURIComponent(query)}`}
                      onClick={() => {
                        setIsSearchOpen(false)
                      }}
                      className='flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-[1.02]'
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        color: 'var(--primary-ink)',
                      }}
                    >
                      See all results for "{query}"
                      <ArrowRight className='w-4 h-4' />
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Empty State - Show Suggestions */}
            {!isSearching && (
              <div className='py-6'>
                <div
                  className='text-sm font-medium mb-4 text-center'
                  style={{
                    color: 'var(--foreground)',
                  }}
                >
                  Search across blog posts, projects, and services
                </div>
                <div className='flex flex-wrap gap-2 justify-center'>
                  {[
                    {
                      label: 'View All Blogs',
                      query: 'blog',
                      category: 'blog',
                    },
                    {
                      label: 'View All Projects',
                      query: 'project',
                      category: 'project',
                    },
                    {
                      label: 'AI Product Development',
                      query: 'AI Product Development',
                    },
                    {
                      label: 'Brand & Sales Design',
                      query: 'Brand & Sales Design',
                    },
                  ].map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (suggestion.category) {
                          // For category-based searches, we'll filter by category
                          // This will be handled by the search results filtering
                          setQuery(suggestion.query)
                        } else {
                          setQuery(suggestion.query)
                        }
                        searchInputRef.current?.focus()
                      }}
                      className='px-4 py-2 rounded-lg text-xs transition-all hover:scale-105'
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        color: 'var(--muted-foreground)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor =
                          'rgba(0, 0, 0, 0.08)'
                        e.currentTarget.style.color = 'var(--foreground)'
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor =
                          'rgba(0, 0, 0, 0.05)'
                        e.currentTarget.style.color = 'var(--muted-foreground)'
                      }}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
                <div
                  className='text-xs text-center mt-6 opacity-60'
                  style={{
                    color: 'var(--muted-foreground)',
                  }}
                >
                  Press{' '}
                  <kbd className='px-1.5 py-0.5 rounded bg-muted text-xs font-mono'>
                    ⌘K
                  </kbd>{' '}
                  or{' '}
                  <kbd className='px-1.5 py-0.5 rounded bg-muted text-xs font-mono'>
                    Ctrl+K
                  </kbd>{' '}
                  to search
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden='true'
      />

      {/* Mobile Menu Panel */}
      <div
        ref={mobileMenuRef}
        id='mobile-menu'
        role='dialog'
        aria-modal='true'
        aria-label='Site menu'
        tabIndex={-1}
        inert={!isMobileMenuOpen}
        className={`lg:hidden fixed inset-0 z-50 transform transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          backgroundColor: 'var(--background)',
        }}
      >
        {/* Mobile Menu Header */}
        <div
          className='flex items-center justify-between h-16 px-4 border-b'
          style={{
            borderColor: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          <Link
            href='/'
            onClick={() => {
              setIsMobileMenuOpen(false)
              setIsMobileProductsOpen(false)
              setIsMobileServicesOpen(false)
              setIsMobileWorksOpen(false)
              setIsMobileToolsOpen(false)
              setIsMobileBlogOpen(false)
            }}
            className='inline-flex min-h-11 items-center hover:opacity-80 transition-opacity'
          >
            <Image
              src='/pixelmojo-branding-new.svg'
              alt='Pixelmojo'
              width={120}
              height={28}
              className='h-7 w-auto transition-all duration-300'
            />
          </Link>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false)
              setIsMobileProductsOpen(false)
              setIsMobileServicesOpen(false)
              setIsMobileWorksOpen(false)
              setIsMobileToolsOpen(false)
              setIsMobileBlogOpen(false)
            }}
            className='p-3 -mr-3 transition-colors touch-manipulation'
            style={{
              minHeight: '44px',
              minWidth: '44px',
              color: 'var(--muted-foreground)',
            }}
            aria-label='Close menu'
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Mobile Menu Content */}
        <div className='h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain'>
          <nav className='p-6'>
            {/* Main Navigation with Integrated Services Dropdown */}
            <ul className='space-y-2 mb-8'>
              {navigationConfig.mainNav.map(item => (
                <li key={item.label}>
                  {item.children ? (
                    // Dropdown items (Services/Projects)
                    <div>
                      <button
                        aria-expanded={
                          (item.label === 'Products' && isMobileProductsOpen) ||
                          (item.label === 'Services' && isMobileServicesOpen) ||
                          (item.label === 'Projects' && isMobileWorksOpen) ||
                          (item.label === 'Tools' && isMobileToolsOpen) ||
                          (item.label === 'Blog' && isMobileBlogOpen)
                        }
                        aria-controls={`mobile-submenu-${item.label}`}
                        onClick={() => {
                          if (item.label === 'Products') {
                            setIsMobileProductsOpen(!isMobileProductsOpen)
                            setIsMobileServicesOpen(false)
                            setIsMobileWorksOpen(false)
                            setIsMobileToolsOpen(false)
                            setIsMobileBlogOpen(false)
                          } else if (item.label === 'Services') {
                            setIsMobileServicesOpen(!isMobileServicesOpen)
                            setIsMobileProductsOpen(false)
                            setIsMobileWorksOpen(false)
                            setIsMobileToolsOpen(false)
                            setIsMobileBlogOpen(false)
                          } else if (item.label === 'Projects') {
                            setIsMobileWorksOpen(!isMobileWorksOpen)
                            setIsMobileProductsOpen(false)
                            setIsMobileServicesOpen(false)
                            setIsMobileToolsOpen(false)
                            setIsMobileBlogOpen(false)
                          } else if (item.label === 'Tools') {
                            setIsMobileToolsOpen(!isMobileToolsOpen)
                            setIsMobileProductsOpen(false)
                            setIsMobileServicesOpen(false)
                            setIsMobileWorksOpen(false)
                            setIsMobileBlogOpen(false)
                          } else if (item.label === 'Blog') {
                            setIsMobileBlogOpen(!isMobileBlogOpen)
                            setIsMobileProductsOpen(false)
                            setIsMobileServicesOpen(false)
                            setIsMobileWorksOpen(false)
                            setIsMobileToolsOpen(false)
                          }
                        }}
                        className={`w-full flex items-center justify-between text-lg font-medium transition-colors duration-200 py-4 px-4 rounded-lg hover:bg-muted/50 touch-manipulation relative ${
                          isActiveNav(item.href, true, item.label)
                            ? 'text-primary-ink bg-primary/5 border-l-4 border-primary'
                            : 'hover:text-primary-ink'
                        }`}
                        style={{
                          minHeight: '44px',
                          color: 'var(--foreground)',
                        }}
                      >
                        <span>{item.label}</span>
                        <svg
                          aria-hidden='true'
                          className={`w-5 h-5 transition-transform duration-200 ${
                            (item.label === 'Products' &&
                              isMobileProductsOpen) ||
                            (item.label === 'Services' &&
                              isMobileServicesOpen) ||
                            (item.label === 'Projects' && isMobileWorksOpen) ||
                            (item.label === 'Tools' && isMobileToolsOpen) ||
                            (item.label === 'Blog' && isMobileBlogOpen)
                              ? 'rotate-180'
                              : ''
                          }`}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                          />
                        </svg>
                      </button>

                      {/* Dropdown content */}
                      <div
                        id={`mobile-submenu-${item.label}`}
                        inert={
                          !(
                            (item.label === 'Products' &&
                              isMobileProductsOpen) ||
                            (item.label === 'Services' &&
                              isMobileServicesOpen) ||
                            (item.label === 'Projects' && isMobileWorksOpen) ||
                            (item.label === 'Tools' && isMobileToolsOpen) ||
                            (item.label === 'Blog' && isMobileBlogOpen)
                          )
                        }
                        className={`overflow-hidden transition-all duration-300 ${
                          (item.label === 'Products' && isMobileProductsOpen) ||
                          (item.label === 'Services' && isMobileServicesOpen) ||
                          (item.label === 'Projects' && isMobileWorksOpen) ||
                          (item.label === 'Tools' && isMobileToolsOpen) ||
                          (item.label === 'Blog' && isMobileBlogOpen)
                            ? 'max-h-96'
                            : 'max-h-0'
                        }`}
                      >
                        <ul className='ml-4 mt-2 space-y-1 border-l-2 border-border pl-4'>
                          {item.children.map(subItem => (
                            <li key={subItem.href}>
                              <Link
                                href={subItem.href}
                                onClick={() => {
                                  setIsMobileMenuOpen(false)
                                  setIsMobileProductsOpen(false)
                                  setIsMobileServicesOpen(false)
                                  setIsMobileWorksOpen(false)
                                  setIsMobileToolsOpen(false)
                                  setIsMobileBlogOpen(false)
                                }}
                                className='flex min-h-11 items-center px-3 py-2 text-base hover:text-primary-ink transition-colors duration-200 rounded hover:bg-muted/30'
                                style={{
                                  color: 'var(--muted-foreground)',
                                }}
                              >
                                {subItem.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    // Regular menu items
                    <Link
                      href={item.href}
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        setIsMobileProductsOpen(false)
                        setIsMobileServicesOpen(false)
                        setIsMobileWorksOpen(false)
                        setIsMobileToolsOpen(false)
                        setIsMobileBlogOpen(false)
                      }}
                      className={`block text-lg font-medium transition-colors duration-200 py-4 px-4 rounded-lg hover:bg-muted/50 touch-manipulation ${
                        isActiveNav(item.href)
                          ? 'text-primary-ink bg-primary/5 border-l-4 border-primary'
                          : 'hover:text-primary-ink'
                      }`}
                      style={{
                        minHeight: '44px',
                        color: 'var(--foreground)',
                      }}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Mobile CTA */}
            <div className='px-4 space-y-4'>
              <SignInLink
                className='flex items-center justify-center w-full min-h-[44px] rounded-full border text-base font-semibold transition-all hover:opacity-80'
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
                signedOutLabel='Sign in to Radar'
                signedInLabel='Open Account'
              />

              <LinkButton
                href={navigationConfig.ctaButton.href}
                data-cta-id='header'
                variant='default'
                size='lg'
                className='w-full min-h-[44px]'
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  setIsMobileProductsOpen(false)
                  setIsMobileServicesOpen(false)
                  setIsMobileWorksOpen(false)
                  setIsMobileToolsOpen(false)
                  setIsMobileBlogOpen(false)
                }}
              >
                {navigationConfig.ctaButton.label}
              </LinkButton>

              {/* Mobile language switcher — self-hides off translated routes */}
              <div className='flex items-center justify-center'>
                <LocaleSwitcher />
              </div>

              {/* Mobile Theme Toggle */}
              <div className='flex items-center justify-center'>
                <div className='flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30'>
                  <span
                    className='text-sm font-medium'
                    style={{
                      color: 'var(--foreground)',
                    }}
                  >
                    Theme
                  </span>
                  <AnimatedThemeToggle
                    className='min-h-11 min-w-11'
                    showScrollTooltip={false}
                  />
                </div>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  )
}
